import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Smartphone } from '@mui/icons-material';

interface MobilePreviewProps {
  children: React.ReactNode;
  title?: string;
}

/**
 * Composant qui simule un écran de téléphone mobile pour la prévisualisation
 * Dimensions basées sur iPhone 14 (390x844 px)
 */
const MobilePreview: React.FC<MobilePreviewProps> = ({ children, title = 'Aperçu Mobile' }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '600px',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          px: 2,
          py: 1,
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 1,
        }}
      >
        <Smartphone />
        <Typography variant="h6">{title}</Typography>
      </Box>

      {/* Phone Frame */}
      <Paper
        elevation={8}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '390px',
          maxWidth: '100%',
          mx: 'auto',
          bgcolor: '#000',
          borderRadius: '36px',
          padding: '12px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Notch (encoche du haut) */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '150px',
            height: '30px',
            bgcolor: '#000',
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
            zIndex: 10,
          }}
        />

        {/* Screen Content */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#f5f5f5',
            borderRadius: '24px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Status Bar */}
          <Box
            sx={{
              height: '44px',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              pt: 1,
              fontSize: '12px',
              fontWeight: 600,
              color: '#000',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <span>8:23</span>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Box
                sx={{
                  width: '18px',
                  height: '12px',
                  border: '1.5px solid #000',
                  borderRadius: '3px',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: '-3px',
                    top: '3px',
                    width: '2px',
                    height: '4px',
                    bgcolor: '#000',
                    borderRadius: '1px',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: '1.5px',
                    top: '1.5px',
                    bottom: '1.5px',
                    width: '60%',
                    bgcolor: '#000',
                    borderRadius: '1px',
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              height: 'calc(100% - 44px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              bgcolor: '#f5f5f5',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'rgba(0,0,0,0.2)',
                borderRadius: '3px',
              },
            }}
          >
            {children}
          </Box>
        </Box>

        {/* Home Indicator (barre du bas iOS) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '140px',
            height: '5px',
            bgcolor: '#ffffff',
            borderRadius: '3px',
            opacity: 0.5,
          }}
        />
      </Paper>
    </Box>
  );
};

export default MobilePreview;
